import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Divider,
  Table,
  Tag,
  Spin,
  Result,
  Modal,
  Input,
  message,
  Alert,
  Progress,
  Empty,
  Radio,
  Steps,
  DatePicker,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  CarOutlined,
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { calculateEstimateTotals } from '../utils/estimateTotals';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface Estimate {
  id: number;
  estimate_number: string;
  status: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_company_name?: string;
  customer_phone: string;
  customer_email: string;
  year: number;
  make: string;
  model: string;
  vin: string;
  license_plate: string;
  customer_notes?: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  valid_until?: string;
  disclaimer?: string;
  line_items: any[];
  signed_at?: string;
  signature_data?: string;
  signature_type?: 'remote' | 'in_person';
}

interface Part {
  id: number;
  part_number: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percentage?: number;
  discount_amount?: number;
  discounted_price?: number;
}

const PublicEstimateView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const isInPerson = searchParams.get('inperson') === 'true';
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [signatureRef, setSignatureRef] = useState<SignatureCanvas | null>(null);
  const [inPersonSignatureRef, setInPersonSignatureRef] = useState<SignatureCanvas | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lineItemStatus, setLineItemStatus] = useState<{ [key: number]: 'approved' | 'declined' | 'unselected' }>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Appointment booking state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [canSchedule, setCanSchedule] = useState<any>(null);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingProcessing, setBookingProcessing] = useState(false);
  const [existingAppointment, setExistingAppointment] = useState<any>(null);
  const [bookingSettings, setBookingSettings] = useState<any>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (token) {
      fetchEstimate();
    }
  }, [token]);

  // Scroll to booking section when estimate is approved
  useEffect(() => {
    if (estimate?.status === 'approved' && estimate?.signed_at && !existingAppointment) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 500);
    }
  }, [estimate?.status, estimate?.signed_at, existingAppointment]);

  const fetchEstimate = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.get(`${API_BASE_URL}/public/estimates/${token}`);
      const estimateData = response.data;
      setEstimate(estimateData);

      // Expand all rows by default
      if (estimateData.line_items) {
        const allKeys = estimateData.line_items.map((item: any) => item.id);
        setExpandedRowKeys(allKeys);

        // Initialize all line items as unselected by default
        const initialStatus: { [key: number]: 'approved' | 'declined' | 'unselected' } = {};
        estimateData.line_items.forEach((item: any) => {
          initialStatus[item.id] = 'unselected';
        });
        setLineItemStatus(initialStatus);
      }
    } catch (err: any) {
      console.error('Error fetching estimate:', err);
      setError(err.response?.data?.error || 'Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = () => {
    setShowSignatureModal(true);
  };

  const handleSignatureSubmit = async () => {
    const activeRef = isInPerson ? inPersonSignatureRef : signatureRef;
    if (!activeRef || activeRef.isEmpty()) {
      message.warning('Please provide your signature');
      return;
    }

    // Check if at least one item is approved
    const hasApprovedItems = Object.values(lineItemStatus).some(status => status === 'approved');
    if (!hasApprovedItems) {
      message.warning('Please approve at least one line item');
      return;
    }

    try {
      setProcessing(true);
      const signatureData = activeRef.toDataURL();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      await axios.post(`${API_BASE_URL}/public/estimates/${token}/approve`, {
        signature: signatureData,
        lineItemStatus: lineItemStatus,
        signature_type: isInPerson ? 'in_person' : 'remote',
      });

      message.success('Estimate approved successfully! Redirecting...', 3);
      setShowSignatureModal(false);
      fetchEstimate(); // Reload to show approved status
    } catch (err: any) {
      console.error('Error approving estimate:', err);
      const errorMsg = err.response?.data?.error || 'Failed to approve estimate. Please try again.';
      message.error(errorMsg, 5);
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    try {
      setProcessing(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      await axios.post(`${API_BASE_URL}/public/estimates/${token}/decline`, {
        reason: declineReason,
      });

      message.info('Estimate declined. Thank you for your feedback.', 3);
      setShowDeclineModal(false);
      fetchEstimate(); // Reload to show declined status
    } catch (err: any) {
      console.error('Error declining estimate:', err);
      const errorMsg = err.response?.data?.error || 'Failed to decline estimate. Please try again.';
      message.error(errorMsg, 5);
    } finally {
      setProcessing(false);
    }
  };

  // Appointment booking functions
  const loadBookingSettings = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.get(`${API_BASE_URL}/public/appointments/settings`);
      setBookingSettings(response.data.booking);
      return response.data.booking;
    } catch (err: any) {
      console.error('Error loading booking settings:', err);
      return null;
    }
  };

  const checkCanSchedule = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await axios.get(`${API_BASE_URL}/public/estimates/${token}/can-schedule`);
      setCanSchedule(response.data);
      return response.data;
    } catch (err: any) {
      console.error('Error checking schedule capability:', err);
      return null;
    }
  };

  const loadAvailableSlots = async (date: Dayjs) => {
    if (!date || !estimate) return;

    setBookingLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      // Calculate duration from estimate line items
      const totalHours = estimate.line_items.reduce((sum: number, item: any) => {
        return sum + (item.labor_hours || 0);
      }, 0);
      const durationMinutes = Math.round(totalHours * 60) || 60; // Default to 1 hour if no labor hours

      const startDate = date.format('YYYY-MM-DD');
      const endDate = date.format('YYYY-MM-DD');

      const response = await axios.get(`${API_BASE_URL}/public/appointments/availability`, {
        params: {
          start_date: `${startDate}T00:00:00`,
          end_date: `${endDate}T23:59:59`,
          duration_minutes: durationMinutes,
          appointment_type: 'repair'
        }
      });

      const availableOnly = response.data.filter((slot: any) => slot.available);

      // Deduplicate slots by time - since backend auto-assigns techs, customers only need to see unique times
      const uniqueSlots = availableOnly.reduce((acc: any[], slot: any) => {
        const isDuplicate = acc.some((s: any) =>
          s.start_time === slot.start_time && s.end_time === slot.end_time
        );
        if (!isDuplicate) {
          acc.push(slot);
        }
        return acc;
      }, []);

      console.log(`[BOOKING] Loaded ${response.data.length} total slots, ${availableOnly.length} available, ${uniqueSlots.length} unique time slots`);
      setAvailableSlots(uniqueSlots);
    } catch (err: any) {
      console.error('Error loading available slots:', err);
      message.error(err.response?.data?.error || 'Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setBookingLoading(false);
    }
  };

  // Load slots when date changes
  useEffect(() => {
    if (selectedDate && showBookingModal) {
      loadAvailableSlots(selectedDate);
    }
  }, [selectedDate, showBookingModal]);

  const handleBookAppointment = async () => {
    if (!selectedSlot) {
      message.error('Please select a time slot');
      return;
    }

    setBookingProcessing(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      await axios.post(`${API_BASE_URL}/public/estimates/${token}/book-appointment`, {
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: bookingNotes || undefined
        // Note: technician_id is NOT sent - backend will auto-assign best available technician
      });

      message.success('Appointment booked successfully! A technician will be assigned to your appointment.', 5);
      setShowBookingModal(false);
      setExistingAppointment({
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        technician_name: 'Assigned' // Will be assigned by system
      });
    } catch (err: any) {
      console.error('Error booking appointment:', err);
      const errorMessage = err.response?.data?.error || 'Failed to book appointment. Please try again.';
      message.error(errorMessage, 5);
    } finally {
      setBookingProcessing(false);
    }
  };

  const openBookingModal = async () => {
    const canSched = await checkCanSchedule();
    if (canSched?.can_schedule) {
      // Load booking settings
      const settings = await loadBookingSettings();
      if (settings) {
        setShowBookingModal(true);
        // Auto-select tomorrow as default date
        const tomorrow = dayjs().add(settings.booking_advance_days_min || 1, 'day');
        setSelectedDate(tomorrow);
      }
    } else {
      message.error(canSched?.reason || 'Cannot schedule this estimate');
    }
  };

  const disabledDate = (current: Dayjs) => {
    if (!bookingSettings) return true;

    const minDate = dayjs().add(bookingSettings.booking_advance_days_min, 'day');
    const maxDate = dayjs().add(bookingSettings.booking_advance_days_max, 'day');

    return current && (current < minDate || current > maxDate || current.day() === 0);
  };

  const getWorkloadColor = (percent: number) => {
    if (percent >= 80) return 'exception';
    if (percent >= 60) return 'normal';
    return 'success';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'blue';
    if (score >= 40) return 'orange';
    return 'default';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Loading estimate..." />
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div style={{ padding: '40px' }}>
        <Result
          status="404"
          title="Estimate Not Found"
          subTitle={error || 'The estimate you are looking for does not exist or the link has expired.'}
          extra={
            <Button type="primary" onClick={() => navigate('/')}>
              Go Home
            </Button>
          }
        />
      </div>
    );
  }

  const handleLineItemStatusChange = (lineItemId: number, status: 'approved' | 'declined' | 'unselected') => {
    setLineItemStatus(prev => ({
      ...prev,
      [lineItemId]: status,
    }));
  };

  const handleApproveAll = () => {
    if (!estimate) return;
    const allApproved: { [key: number]: 'approved' | 'declined' | 'unselected' } = {};
    estimate.line_items.forEach((item: any) => {
      allApproved[item.id] = 'approved';
    });
    setLineItemStatus(allApproved);
    setShowSignatureModal(true);
  };

  const handleDeclineAll = () => {
    setShowDeclineModal(true);
  };

  const lineItemColumns = [
    {
      title: 'Select',
      key: 'status',
      width: 160,
      render: (record: any) => {
        const status = lineItemStatus[record.id];
        if (!isActionable) {
          return status === 'approved' ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>Approved</Tag>
          ) : status === 'declined' ? (
            <Tag color="error" icon={<CloseCircleOutlined />}>Declined</Tag>
          ) : (
            <Tag>Not Selected</Tag>
          );
        }
        return (
          <Space size="small">
            <Button
              type={status === 'approved' ? 'primary' : 'default'}
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleLineItemStatusChange(record.id, 'approved')}
              disabled={processing}
              style={{ background: status === 'approved' ? '#10b981' : undefined, borderColor: status === 'approved' ? '#10b981' : undefined }}
            >
              {status === 'approved' ? '✓' : 'Select'}
            </Button>
            <Button
              type={status === 'declined' ? 'primary' : 'default'}
              danger={status === 'declined'}
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => handleLineItemStatusChange(record.id, 'declined')}
              disabled={processing}
            >
              {status === 'declined' ? '✗' : 'Decline'}
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: '500' }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.category}</Text>
        </div>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Labor',
      key: 'labor',
      width: 150,
      align: 'center' as const,
      render: (record: any) => {
        if (!record.labor_hours || !record.hourly_rate) return '-';
        const baseLaborCost = record.labor_hours * record.hourly_rate;
        const hasDiscount = record.labor_discount_percentage || record.labor_discount_amount;
        const laborDiscountAmount = record.labor_discount_amount ||
          (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;

        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px' }}>{record.labor_hours} hrs @ ${record.hourly_rate}/hr</div>
            {hasDiscount ? (
              <>
                <Text type="secondary" delete style={{ fontSize: '11px', display: 'block' }}>
                  ${baseLaborCost.toFixed(2)}
                </Text>
                <Text style={{ color: '#10b981', fontWeight: '500' }}>${discountedLaborCost.toFixed(2)}</Text>
                <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                  -{record.labor_discount_percentage
                    ? `${record.labor_discount_percentage}%`
                    : `$${laborDiscountAmount.toFixed(2)}`}
                </Text>
              </>
            ) : (
              <Text style={{ color: '#10b981', fontWeight: '500' }}>${baseLaborCost.toFixed(2)}</Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Parts',
      key: 'parts',
      width: 80,
      align: 'center' as const,
      render: (record: any) => {
        const partsCount = record.parts?.length || 0;
        return partsCount > 0 ? (
          <Tag color="blue">{partsCount} part{partsCount !== 1 ? 's' : ''}</Tag>
        ) : '-';
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 120,
      align: 'right' as const,
      render: (price: number, record: any) => {
        const status = lineItemStatus[record.id];
        const isDeclined = status === 'declined';
        const isUnselected = status === 'unselected';
        return (
          <Text
            strong
            style={{
              color: isDeclined || isUnselected ? '#999' : '#10b981',
              textDecoration: isDeclined ? 'line-through' : 'none',
              opacity: isUnselected ? 0.5 : 1
            }}
          >
            ${price.toFixed(2)}
          </Text>
        );
      },
    },
  ];

  const partColumns = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'center' as const,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => `$${price.toFixed(2)}`,
    },
    {
      title: 'Total',
      key: 'total_price',
      width: 120,
      align: 'right' as const,
      render: (record: Part) => {
        const hasDiscount = record.discount_percentage || record.discount_amount;
        const basePrice = record.quantity * record.unit_price;

        if (hasDiscount && record.discounted_price !== undefined) {
          return (
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary" delete style={{ fontSize: '11px', display: 'block' }}>
                ${basePrice.toFixed(2)}
              </Text>
              <Text style={{ color: '#10b981', fontWeight: '500' }}>${record.discounted_price.toFixed(2)}</Text>
              <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                -{record.discount_percentage
                  ? `${record.discount_percentage}%`
                  : `$${(record.discount_amount || 0).toFixed(2)}`}
              </Text>
            </div>
          );
        }

        return <Text style={{ color: '#10b981' }}>${record.total_price.toFixed(2)}</Text>;
      },
    },
  ];

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: 'Draft' },
      sent: { color: 'processing', text: 'Sent' },
      approved: { color: 'success', text: 'Approved' },
      declined: { color: 'error', text: 'Declined' },
      converted: { color: 'purple', text: 'In Progress' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const isActionable = estimate.status === 'sent' || estimate.status === 'draft';

  // Mobile Card View for Line Items
  const renderMobileLineItems = () => {
    if (!estimate) return null;

    return estimate.line_items.map((item: any) => {
      const status = lineItemStatus[item.id];
      const isDeclined = status === 'declined';
      const isUnselected = status === 'unselected';
      const isApproved = status === 'approved';

      return (
        <Card
          key={item.id}
          size="small"
          style={{
            marginBottom: 12,
            borderColor: isApproved ? '#10b981' : isDeclined ? '#ef4444' : '#e5e7eb',
            borderWidth: 2,
            opacity: isUnselected ? 0.7 : 1,
          }}
        >
          {/* Status Buttons */}
          {isActionable && (
            <Space style={{ width: '100%', marginBottom: 12 }} size="small">
              <Button
                type={isApproved ? 'primary' : 'default'}
                size="small"
                block
                icon={<CheckCircleOutlined />}
                onClick={() => handleLineItemStatusChange(item.id, 'approved')}
                disabled={processing}
                style={{
                  flex: 1,
                  background: isApproved ? '#10b981' : undefined,
                  borderColor: isApproved ? '#10b981' : undefined
                }}
              >
                {isApproved ? '✓ Selected' : 'Select'}
              </Button>
              <Button
                type={isDeclined ? 'primary' : 'default'}
                danger={isDeclined}
                size="small"
                block
                icon={<CloseCircleOutlined />}
                onClick={() => handleLineItemStatusChange(item.id, 'declined')}
                disabled={processing}
                style={{ flex: 1 }}
              >
                {isDeclined ? '✗ Declined' : 'Decline'}
              </Button>
            </Space>
          )}

          {/* Item Details */}
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ display: 'block', fontSize: 16 }}>{item.description}</Text>
            <Tag color="blue" style={{ marginTop: 4 }}>{item.category}</Tag>
          </div>

          {/* Labor Info */}
          {item.labor_hours && item.hourly_rate && (
            <div style={{
              background: '#f9fafb',
              padding: 8,
              borderRadius: 6,
              marginBottom: 8
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Labor</Text>
              <div>
                <Text strong>{item.labor_hours} hrs @ ${item.hourly_rate}/hr</Text>
                {(() => {
                  const baseLaborCost = item.labor_hours * item.hourly_rate;
                  const hasDiscount = item.labor_discount_percentage || item.labor_discount_amount;
                  const laborDiscountAmount = item.labor_discount_amount ||
                    (baseLaborCost * (item.labor_discount_percentage || 0) / 100);
                  const discountedLaborCost = baseLaborCost - laborDiscountAmount;

                  return hasDiscount ? (
                    <div style={{ float: 'right', textAlign: 'right' }}>
                      <Text type="secondary" delete style={{ fontSize: 11 }}>
                        ${baseLaborCost.toFixed(2)}
                      </Text>
                      <br />
                      <Text style={{ color: '#10b981', fontWeight: '600' }}>
                        ${discountedLaborCost.toFixed(2)}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        -{item.labor_discount_percentage
                          ? `${item.labor_discount_percentage}%`
                          : `$${laborDiscountAmount.toFixed(2)}`}
                      </Text>
                    </div>
                  ) : (
                    <Text style={{ float: 'right', color: '#10b981' }}>
                      ${baseLaborCost.toFixed(2)}
                    </Text>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Parts */}
          {item.parts && item.parts.length > 0 && (
            <div style={{
              background: '#f9fafb',
              padding: 8,
              borderRadius: 6,
              marginBottom: 8
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Parts ({item.parts.length})
              </Text>
              {item.parts.map((part: any, idx: number) => {
                const hasDiscount = part.discount_percentage || part.discount_amount;
                const basePrice = part.quantity * part.unit_price;

                return (
                  <div key={part.id || idx} style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text style={{ fontSize: 13 }}>{part.part_number} - {part.description}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Qty {part.quantity} × ${part.unit_price.toFixed(2)}
                      </Text>
                      {hasDiscount && part.discounted_price !== undefined ? (
                        <div style={{ textAlign: 'right' }}>
                          <Text type="secondary" delete style={{ fontSize: 11 }}>
                            ${basePrice.toFixed(2)}
                          </Text>
                          <br />
                          <Text style={{ fontSize: 12, color: '#10b981', fontWeight: '600' }}>
                            ${part.discounted_price.toFixed(2)}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            -{part.discount_percentage
                              ? `${part.discount_percentage}%`
                              : `$${(part.discount_amount || 0).toFixed(2)}`}
                          </Text>
                        </div>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ${part.total_price.toFixed(2)}
                        </Text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quantity */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">Quantity: {item.quantity}</Text>
            <Text
              strong
              style={{
                fontSize: 18,
                color: isDeclined || isUnselected ? '#999' : '#10b981',
                textDecoration: isDeclined ? 'line-through' : 'none'
              }}
            >
              ${item.total_price.toFixed(2)}
            </Text>
          </div>
        </Card>
      );
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, rgba(239, 246, 255, 0.2) 100%)',
      padding: isMobile ? '16px 12px' : '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Booking CTA - Show prominently at top when approved */}
        {estimate.status === 'approved' && estimate.signed_at && !existingAppointment && (
          <Card
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #1890ff',
              marginBottom: 24
            }}
          >
            <Result
              status="success"
              title={
                <Title level={2} style={{ color: '#1890ff', margin: 0 }}>
                  🎉 Congratulations! Your Estimate is Approved!
                </Title>
              }
              subTitle={
                <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 600, margin: '20px auto' }}>
                  <Card style={{ background: 'white', textAlign: 'center' }}>
                    <Space direction="vertical" size="middle">
                      <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                      <Title level={4} style={{ margin: 0 }}>You're in the Right Hands!</Title>
                      <Paragraph style={{ fontSize: 16, margin: 0 }}>
                        Thank you for approving your estimate. Our expert technicians are ready to take care of your vehicle.
                      </Paragraph>
                      <Divider style={{ margin: '12px 0' }} />
                      <Alert
                        message="Next Step: Book Your Appointment"
                        description="Please select a convenient time below so we can schedule your service and get you back on the road!"
                        type="info"
                        showIcon
                        icon={<CalendarOutlined />}
                        style={{ textAlign: 'left' }}
                      />
                    </Space>
                  </Card>

                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<CalendarOutlined />}
                    onClick={openBookingModal}
                    style={{
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none',
                      height: 60,
                      fontSize: isMobile ? 16 : 18,
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
                      animation: 'pulse 2s infinite',
                    }}
                  >
                    {isMobile ? 'Book Appointment Now' : 'Book Your Appointment Now'}
                  </Button>
                  <style>{`
                    @keyframes pulse {
                      0%, 100% {
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
                        transform: scale(1);
                      }
                      50% {
                        box-shadow: 0 6px 20px rgba(24, 144, 255, 0.6);
                        transform: scale(1.02);
                      }
                    }
                  `}</style>

                  <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center' }}>
                    Approved on {new Date(estimate.signed_at).toLocaleString()}
                  </Text>
                </Space>
              }
            />
          </Card>
        )}

        {/* Header */}
        {!isInPerson && (<Card style={{ marginBottom: isMobile ? 16 : 24, borderRadius: 12 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>
                  Estimate #{estimate.estimate_number}
                </Title>
                <Space wrap>
                  {getStatusTag(estimate.status)}
                  {estimate.valid_until && (
                    <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
                      <CalendarOutlined /> Valid until {new Date(estimate.valid_until).toLocaleDateString()}
                    </Text>
                  )}
                </Space>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              {(() => {
                const approvedSubtotal = estimate.line_items.reduce((sum: number, item: any) => {
                  return lineItemStatus[item.id] === 'approved' ? sum + item.total_price : sum;
                }, 0);
                const approvedTax = (approvedSubtotal * estimate.tax_rate) / 100;
                const approvedTotal = approvedSubtotal + approvedTax;
                const hasSelections = Object.values(lineItemStatus).some(s => s === 'approved');

                return (
                  <div style={{ textAlign: 'right' }}>
                    {hasSelections ? (
                      <>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Selected Total:</Text>
                        <Title level={2} style={{ margin: 0, color: '#10b981' }}>
                          ${approvedTotal.toFixed(2)}
                        </Title>
                      </>
                    ) : (
                      <>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Full Estimate:</Text>
                        <Title level={2} style={{ margin: 0, color: '#999' }}>
                          ${estimate.total_amount.toFixed(2)}
                        </Title>
                      </>
                    )}
                  </div>
                );
              })()}
            </Col>
          </Row>
        </Card>
        )}

        {/* Customer & Vehicle Info */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12}>
            <Card title={<><UserOutlined /> Customer Information</>} variant="borderless">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>
                  {estimate.customer_company_name || `${estimate.customer_first_name} ${estimate.customer_last_name}`}
                </Text>
                <Text type="secondary">{estimate.customer_email}</Text>
                <Text type="secondary">{estimate.customer_phone}</Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title={<><CarOutlined /> Vehicle Information</>} variant="borderless">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>{estimate.year} {estimate.make} {estimate.model}</Text>
                <Text type="secondary">VIN: {estimate.vin}</Text>
                <Text type="secondary">License: {estimate.license_plate}</Text>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Services */}
        <Card
          title={<><FileTextOutlined /> Services & Parts</>}
          style={{ marginBottom: 24 }}
        >
          {isActionable && (
            <Alert
              message="Review Services"
              description={isMobile
                ? "Tap Select or Decline on each service. Use buttons at bottom to approve all or sign selected."
                : "Review each service below. You can select individual services you want, or use the buttons at the bottom to approve all or decline all. The total updates in real-time as you make selections."
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Card View (≤1085px) */}
          {isMobile ? (
            <div>{renderMobileLineItems()}</div>
          ) : (
            /* Table View (>1085px) */
            <Table
              dataSource={estimate.line_items}
              columns={lineItemColumns}
              pagination={false}
              rowKey="id"
              size="small"
              expandedRowKeys={expandedRowKeys}
              onExpandedRowsChange={(keys) => setExpandedRowKeys(keys as number[])}
              expandable={{
                expandedRowRender: (record: any) => {
                  if (!record.parts || record.parts.length === 0) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                        No parts required for this service
                      </div>
                    );
                  }
                  return (
                    <div style={{ margin: 0 }}>
                      <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                        Parts Required:
                      </Text>
                      <Table
                        columns={partColumns}
                        dataSource={record.parts}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        showHeader={true}
                        style={{ marginLeft: '24px' }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => record.parts && record.parts.length > 0,
              }}
            />
          )}

          {estimate.customer_notes && (
            <>
              <Divider />
              <div>
                <Text strong>Notes:</Text>
                <Paragraph style={{ marginTop: 8 }}>{estimate.customer_notes}</Paragraph>
              </div>
            </>
          )}

          <Divider />

          {/* Totals */}
          <Row justify={isMobile ? 'center' : 'end'}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Space direction="vertical" size={8} style={{ width: '100%', fontSize: isMobile ? 14 : undefined }}>
                {(() => {
                  const hasApprovedItems = Object.values(lineItemStatus).some(s => s === 'approved');
                  const hasUnapprovedItems = Object.values(lineItemStatus).some(s => s !== 'approved');

                  // Use shared helper for correct pre-discount math
                  const totals = calculateEstimateTotals(
                    estimate.line_items,
                    estimate.tax_rate,
                    hasApprovedItems ? lineItemStatus : undefined
                  );

                  return (
                    <>
                      {!hasApprovedItems && isActionable && (
                        <Row justify="space-between">
                          <Text type="secondary">Full Estimate Subtotal:</Text>
                          <Text type="secondary">${totals.preDiscountSubtotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      {hasApprovedItems && hasUnapprovedItems && (
                        <Row justify="space-between">
                          <Text type="secondary" style={{ fontSize: 12 }}>Full Estimate:</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            ${calculateEstimateTotals(estimate.line_items, estimate.tax_rate).preDiscountSubtotal.toFixed(2)}
                          </Text>
                        </Row>
                      )}
                      {hasApprovedItems && (
                        <Row justify="space-between">
                          <Text>{hasUnapprovedItems ? 'Selected Subtotal (before discounts):' : 'Subtotal (before discounts):'}</Text>
                          <Text>${totals.preDiscountSubtotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      {totals.totalDiscounts > 0 && (
                        <Row justify="space-between">
                          <Text style={{ color: '#ef4444' }}>Discounts:</Text>
                          <Text style={{ color: '#ef4444' }}>-${totals.totalDiscounts.toFixed(2)}</Text>
                        </Row>
                      )}
                      {totals.totalDiscounts > 0 && (
                        <Row justify="space-between">
                          <Text>Subtotal:</Text>
                          <Text>${totals.discountedSubtotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      <Row justify="space-between">
                        <Text>Tax ({estimate.tax_rate}%):</Text>
                        <Text>${totals.taxAmount.toFixed(2)}</Text>
                      </Row>
                      <Divider style={{ margin: '8px 0' }} />
                      <Row justify="space-between">
                        <Text strong style={{ fontSize: 16 }}>Total:</Text>
                        <Text strong style={{ fontSize: 16, color: hasApprovedItems ? '#10b981' : '#999' }}>
                          ${totals.total.toFixed(2)}
                        </Text>
                      </Row>
                      {hasApprovedItems && hasUnapprovedItems && (
                        <Row justify="space-between">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            (Based on selected services)
                          </Text>
                        </Row>
                      )}
                    </>
                  );
                })()}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Disclaimer */}
        {estimate.disclaimer && (
          <Card style={{ marginBottom: 24 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {estimate.disclaimer}
            </Text>
          </Card>
        )}

        {/* In-Person Signature Pad */}
        {isInPerson && isActionable && (
          <Card style={{ marginBottom: 24 }}>
            <Title level={4}>Customer Signature</Title>
            <div style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              backgroundColor: '#fafafa',
              touchAction: 'none',
              marginBottom: 12,
            }}>
              <SignatureCanvas
                ref={(ref: SignatureCanvas | null) => setInPersonSignatureRef(ref)}
                penColor="black"
                minWidth={1.5}
                canvasProps={{
                  width: 550,
                  height: 250,
                  style: { width: '100%', height: 250, touchAction: 'none' },
                }}
              />
            </div>
            <Space>
              <Button onClick={() => inPersonSignatureRef?.clear()}>
                Clear Signature
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                onClick={handleSignatureSubmit}
                loading={processing}
              >
                Sign &amp; Approve In Person
              </Button>
            </Space>
          </Card>
        )}

        {/* Action Buttons */}
        {isActionable && (
          <Card>
            {/* Selection Summary */}
            {(() => {
              const approvedCount = Object.values(lineItemStatus).filter(s => s === 'approved').length;
              const declinedCount = Object.values(lineItemStatus).filter(s => s === 'declined').length;
              const totalCount = estimate.line_items.length;

              if (approvedCount > 0) {
                return (
                  <Alert
                    message={`${approvedCount} service${approvedCount !== 1 ? 's' : ''} selected${declinedCount > 0 ? `, ${declinedCount} declined` : ''} (${totalCount} total)`}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              } else {
                return (
                  <Alert
                    message="No services selected yet"
                    description="Select services above, or use the buttons below to approve all or decline all"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
            })()}

            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                onClick={handleApproveAll}
                disabled={processing}
                loading={processing}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  height: isMobile ? 50 : 56,
                }}
              >
                {processing ? 'Processing...' : 'Approve All & Sign'}
              </Button>
              {!isInPerson && (
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  disabled={processing || !Object.values(lineItemStatus).some(s => s === 'approved')}
                  loading={processing}
                  style={{ height: isMobile ? 50 : 56 }}
                >
                  {processing ? 'Processing...' : 'Sign Selected Services'}
                </Button>
              )}
              <Button
                danger
                size="large"
                block
                icon={<CloseCircleOutlined />}
                onClick={handleDeclineAll}
                disabled={processing}
                loading={processing}
                style={{ height: isMobile ? 50 : 56 }}
              >
                {processing ? 'Processing...' : 'Decline All'}
              </Button>
            </Space>
          </Card>
        )}

        {existingAppointment && (
          <Card>
            <Result
              status="success"
              title="Appointment Scheduled"
              subTitle={
                <Space direction="vertical" size="small">
                  <Text>Your appointment has been successfully booked!</Text>
                  <Text strong style={{ fontSize: 16 }}>
                    {dayjs(existingAppointment.start_time).format('MMMM D, YYYY [at] h:mm A')}
                  </Text>
                  <Alert
                    message="Technician Assigned"
                    description="A qualified technician has been automatically assigned to your appointment based on availability and expertise."
                    type="success"
                    showIcon
                    icon={<ToolOutlined />}
                    style={{ marginTop: 12 }}
                  />
                  <Text type="secondary">
                    Duration: {dayjs(existingAppointment.end_time).diff(dayjs(existingAppointment.start_time), 'minute')} minutes
                  </Text>
                </Space>
              }
              extra={
                <Text type="secondary">
                  We'll send you a confirmation email shortly with all the details. See you then!
                </Text>
              }
            />
          </Card>
        )}

        {estimate.status === 'declined' && (
          <Card>
            <Result
              status="warning"
              title="Estimate Declined"
              subTitle="This estimate has been declined. The shop will contact you to discuss alternatives."
            />
          </Card>
        )}
      </div>

      {/* Signature Modal */}
      <Modal
        title="Sign Estimate"
        open={showSignatureModal}
        onCancel={() => setShowSignatureModal(false)}
        onOk={handleSignatureSubmit}
        okText={processing ? 'Submitting...' : 'Approve & Submit'}
        confirmLoading={processing}
        okButtonProps={{ disabled: processing }}
        cancelButtonProps={{ disabled: processing }}
        width={isMobile ? '95%' : 600}
        centered
        closable={!processing}
        maskClosable={!processing}
      >
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: isMobile ? 13 : 14 }}>
            Please sign below to approve this estimate:
          </Text>
          <div style={{
            border: '2px dashed #d9d9d9',
            borderRadius: 8,
            backgroundColor: '#fafafa',
            touchAction: 'none'
          }}>
            <SignatureCanvas
              ref={(ref: SignatureCanvas | null) => setSignatureRef(ref)}
              penColor="black"
              minWidth={1.5}
              canvasProps={{
                width: 550,
                height: isMobile ? 200 : 200,
                style: { width: '100%', height: isMobile ? 200 : 200, touchAction: 'none' },
              }}
            />
          </div>
          <Button
            size={isMobile ? 'middle' : 'small'}
            onClick={() => signatureRef?.clear()}
            style={{ marginTop: 12, width: isMobile ? '100%' : 'auto' }}
          >
            Clear Signature
          </Button>
        </div>
      </Modal>

      {/* Decline Modal */}
      <Modal
        title="Decline Estimate"
        open={showDeclineModal}
        onCancel={() => setShowDeclineModal(false)}
        onOk={handleDecline}
        okText={processing ? 'Declining...' : 'Decline'}
        okButtonProps={{ danger: true, disabled: processing }}
        cancelButtonProps={{ disabled: processing }}
        confirmLoading={processing}
        width={isMobile ? '95%' : 520}
        centered
        closable={!processing}
        maskClosable={!processing}
      >
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: isMobile ? 13 : 14 }}>
            Please let us know why you're declining this estimate (optional):
          </Text>
          <TextArea
            rows={isMobile ? 3 : 4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter reason for declining..."
            style={{ fontSize: isMobile ? 14 : 16 }}
          />
        </div>
      </Modal>

      {/* Booking Modal */}
      <Modal
        title="Book Your Appointment"
        open={showBookingModal}
        onCancel={() => {
          setShowBookingModal(false);
          setSelectedDate(null);
          setSelectedSlot(null);
          setAvailableSlots([]);
        }}
        footer={null}
        width={isMobile ? '95%' : 1000}
        centered
        bodyStyle={{
          padding: isMobile ? '16px' : '24px'
        }}
      >
        <div>
          {/* Quick Find Next Available */}
          {!selectedDate && bookingSettings && (
            <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
              <Row align="middle" gutter={16}>
                <Col flex="auto">
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ color: 'white', fontSize: 16 }}>
                      <ThunderboltOutlined /> Find Next Available Appointment
                    </Text>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 14 }}>
                      Quickly book the soonest available time slot
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Button
                    size="large"
                    onClick={() => {
                      const nextDate = dayjs().add(bookingSettings.booking_advance_days_min, 'day');
                      setSelectedDate(nextDate);
                    }}
                    style={{ height: 50 }}
                  >
                    Find Slots
                  </Button>
                </Col>
              </Row>
            </Card>
          )}

          <Row gutter={[24, 24]}>
            {/* Date Picker Column */}
            <Col xs={24} lg={10}>
              <Card title="Select a Date" extra={<CalendarOutlined />}>
                <DatePicker
                  style={{ width: '100%', marginBottom: 16 }}
                  value={selectedDate}
                  onChange={setSelectedDate}
                  disabledDate={disabledDate}
                  size="large"
                  format="MMMM D, YYYY"
                />
                {bookingSettings && (
                  <Alert
                    message="Booking Window"
                    description={`You can book appointments ${bookingSettings.booking_advance_days_min} to ${bookingSettings.booking_advance_days_max} days in advance. Sundays are closed.`}
                    type="info"
                    showIcon
                    icon={<ClockCircleOutlined />}
                  />
                )}
              </Card>
            </Col>

            {/* Time Slots Column */}
            <Col xs={24} lg={14}>
              <Card
                title={selectedDate ? `Available Times - ${selectedDate.format('MMMM D, YYYY')}` : 'Select a date to see available times'}
                extra={selectedDate && <Tag color="blue">{availableSlots.length} slots</Tag>}
              >
                {!selectedDate ? (
                  <Empty
                    description="Please select a date from the calendar"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                  />
                ) : bookingLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin size="large">
                      <div style={{ paddingTop: 20 }}>Loading available time slots...</div>
                    </Spin>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <Empty
                    description="No available time slots for this date"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                  >
                    <Button
                      type="primary"
                      onClick={() => {
                        const nextDay = selectedDate.add(1, 'day');
                        if (!disabledDate(nextDay)) {
                          setSelectedDate(nextDay);
                        }
                      }}
                    >
                      Try Next Day
                    </Button>
                  </Empty>
                ) : (
                  <div>
                    {/* Next Available Highlight */}
                    {availableSlots.length > 0 && (
                      <Card
                        style={{
                          marginBottom: 16,
                          background: '#f6ffed',
                          borderColor: '#52c41a',
                          cursor: 'pointer'
                        }}
                        onClick={() => setSelectedSlot(availableSlots[0])}
                        hoverable
                      >
                        <Row align="middle" gutter={16}>
                          <Col>
                            <ThunderboltOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                          </Col>
                          <Col flex="auto">
                            <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                              Next Available
                            </Text>
                            <br />
                            <Text strong style={{ fontSize: 18 }}>
                              {dayjs(availableSlots[0].start_time).format('h:mm A')} - {dayjs(availableSlots[0].end_time).format('h:mm A')}
                            </Text>
                          </Col>
                          <Col>
                            {selectedSlot === availableSlots[0] && (
                              <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                            )}
                          </Col>
                        </Row>
                      </Card>
                    )}

                    <Divider orientation="left">All Available Times</Divider>

                    {/* Group slots by time of day */}
                    {(() => {
                      const morning = availableSlots.filter(slot => {
                        const hour = dayjs(slot.start_time).hour();
                        return hour >= 6 && hour < 12;
                      });
                      const afternoon = availableSlots.filter(slot => {
                        const hour = dayjs(slot.start_time).hour();
                        return hour >= 12 && hour < 17;
                      });
                      const evening = availableSlots.filter(slot => {
                        const hour = dayjs(slot.start_time).hour();
                        return hour >= 17 && hour < 22;
                      });

                      return (
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                          {morning.length > 0 && (
                            <div>
                              <Text type="secondary" strong>
                                <ClockCircleOutlined /> Morning (6 AM - 12 PM)
                              </Text>
                              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                {morning.map((slot, index) => (
                                  <Col xs={12} sm={8} key={index}>
                                    <Button
                                      block
                                      size="large"
                                      type={selectedSlot === slot ? 'primary' : 'default'}
                                      onClick={() => setSelectedSlot(slot)}
                                      icon={selectedSlot === slot && <CheckCircleOutlined />}
                                    >
                                      {dayjs(slot.start_time).format('h:mm A')}
                                    </Button>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}

                          {afternoon.length > 0 && (
                            <div>
                              <Text type="secondary" strong>
                                <ClockCircleOutlined /> Afternoon (12 PM - 5 PM)
                              </Text>
                              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                {afternoon.map((slot, index) => (
                                  <Col xs={12} sm={8} key={index}>
                                    <Button
                                      block
                                      size="large"
                                      type={selectedSlot === slot ? 'primary' : 'default'}
                                      onClick={() => setSelectedSlot(slot)}
                                      icon={selectedSlot === slot && <CheckCircleOutlined />}
                                    >
                                      {dayjs(slot.start_time).format('h:mm A')}
                                    </Button>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}

                          {evening.length > 0 && (
                            <div>
                              <Text type="secondary" strong>
                                <ClockCircleOutlined /> Evening (5 PM - 10 PM)
                              </Text>
                              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                {evening.map((slot, index) => (
                                  <Col xs={12} sm={8} key={index}>
                                    <Button
                                      block
                                      size="large"
                                      type={selectedSlot === slot ? 'primary' : 'default'}
                                      onClick={() => setSelectedSlot(slot)}
                                      icon={selectedSlot === slot && <CheckCircleOutlined />}
                                    >
                                      {dayjs(slot.start_time).format('h:mm A')}
                                    </Button>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}
                        </Space>
                      );
                    })()}
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Selected Time Summary */}
          {selectedSlot && (
            <Alert
              message="Time Slot Selected"
              description={
                <Space>
                  <CalendarOutlined />
                  <Text strong>
                    {selectedDate?.format('MMMM D, YYYY')} at {dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}
                  </Text>
                </Space>
              }
              type="success"
              showIcon
              style={{ marginTop: 24 }}
            />
          )}

          {/* Additional Notes */}
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Additional Notes (Optional)
            </Text>
            <TextArea
              rows={3}
              value={bookingNotes}
              onChange={(e) => setBookingNotes(e.target.value)}
              placeholder="Any special requests or information we should know?"
              maxLength={500}
            />
          </div>

          {/* Action Buttons */}
          <Divider />
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button
                size="large"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedDate(null);
                  setSelectedSlot(null);
                  setAvailableSlots([]);
                }}
                block
              >
                Cancel
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                size="large"
                onClick={handleBookAppointment}
                loading={bookingProcessing}
                disabled={!selectedSlot || bookingProcessing}
                block
                icon={<CheckCircleOutlined />}
              >
                {bookingProcessing ? 'Booking...' : 'Confirm Appointment'}
              </Button>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default PublicEstimateView;
